/** Preferences section — theme toggle, timezone selector, and week-start-day picker. */
import { useMemo } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { motion } from 'motion/react';
import { Sun, Moon, Monitor, Check } from 'lucide-react';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { UpdateProfileSchema } from '@formency/shared';
import { Button, FormComboBox, Form } from '@/interfaces/learner/ui';
import { useProfile, useUpdateProfile } from '@/shared/api/hooks';
import { useTheme, type Theme } from '@/shared/hooks/useTheme';
import { extractErrorMessage } from '@/shared/utils/extractErrorMessage';
import { SettingsSection } from './SettingsSection';

const EASE = [0.16, 1, 0.3, 1] as const;

// ─── Timezone data ──────────────────────────────────────────────────────────

const TIMEZONE_IDS = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Anchorage',
  'America/Toronto',
  'America/Vancouver',
  'America/Mexico_City',
  'America/Sao_Paulo',
  'America/Argentina/Buenos_Aires',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Madrid',
  'Europe/Rome',
  'Europe/Amsterdam',
  'Europe/Stockholm',
  'Europe/Moscow',
  'Europe/Istanbul',
  'Asia/Dubai',
  'Asia/Kolkata',
  'Asia/Bangkok',
  'Asia/Singapore',
  'Asia/Shanghai',
  'Asia/Hong_Kong',
  'Asia/Tokyo',
  'Asia/Seoul',
  'Australia/Sydney',
  'Australia/Melbourne',
  'Australia/Perth',
  'Pacific/Auckland',
  'Africa/Cairo',
  'Africa/Lagos',
  'Africa/Johannesburg',
  'Africa/Nairobi',
];

function getUtcOffset(tz: string): string {
  try {
    const formatter = new Intl.DateTimeFormat('en-GB', {
      timeZone: tz,
      timeZoneName: 'shortOffset',
    });
    const parts = formatter.formatToParts(new Date());
    const offsetPart = parts.find((p) => p.type === 'timeZoneName');
    return offsetPart?.value?.replace('GMT', 'UTC') ?? '';
  } catch {
    return '';
  }
}

interface TimezoneItem {
  id: string;
  label: string;
}

function buildTimezoneItems(): TimezoneItem[] {
  const items: TimezoneItem[] = [{ id: 'auto', label: 'Auto-detect' }];
  for (const tz of TIMEZONE_IDS) {
    const offset = getUtcOffset(tz);
    items.push({ id: tz, label: `${tz} (${offset})` });
  }
  return items;
}

// ─── Form schema ────────────────────────────────────────────────────────────

const PrefsFormSchema = UpdateProfileSchema.pick({ timezone: true, weekStartsOn: true });
type PrefsFormData = z.infer<typeof PrefsFormSchema>;

// ─── Theme option config ────────────────────────────────────────────────────

const LIGHT_COLORS = { bg: '#f5f5f4', surface: '#ffffff', text: '#1c1917', accent: '#8a6cf0' };
const DARK_COLORS = { bg: '#1c1917', surface: '#292524', text: '#fafaf9', accent: '#9d85f5' };

interface ThemeOption {
  value: Theme;
  label: string;
  description: string;
  icon: typeof Sun;
}

const THEME_OPTIONS: ThemeOption[] = [
  { value: 'light', label: 'Light', description: 'Clean and bright', icon: Sun },
  { value: 'dark', label: 'Dark', description: 'Easier on the eyes', icon: Moon },
  { value: 'system', label: 'System', description: 'Follows your device', icon: Monitor },
];

// ─── Component ──────────────────────────────────────────────────────────────

export default function PreferencesSection() {
  const { data: profile, isLoading } = useProfile();
  const updateMutation = useUpdateProfile();
  const queryClient = useQueryClient();
  const { theme, setTheme, systemTheme } = useTheme();

  const systemColors = systemTheme === 'dark' ? DARK_COLORS : LIGHT_COLORS;
  const previewColors: Record<Theme, typeof LIGHT_COLORS> = {
    light: LIGHT_COLORS,
    dark: DARK_COLORS,
    system: systemColors,
  };

  const timezoneItems = useMemo(() => buildTimezoneItems(), []);
  const detectedTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const formValues: PrefsFormData = {
    timezone: profile?.timezone ?? 'auto',
    weekStartsOn: (profile?.weekStartsOn as 'monday' | 'sunday') ?? 'monday',
  };

  const {
    control,
    handleSubmit,
    formState: { isDirty },
    reset,
    setValue,
  } = useForm<PrefsFormData>({
    resolver: zodResolver(PrefsFormSchema),
    values: formValues,
    mode: 'onBlur',
  });

  const watchedTimezone = useWatch({ control, name: 'timezone' });
  const watchedWeekStart = useWatch({ control, name: 'weekStartsOn' });

  function onSubmit(data: PrefsFormData) {
    updateMutation.mutate(
      {
        timezone: data.timezone === 'auto' ? null : data.timezone,
        weekStartsOn: data.weekStartsOn,
      },
      {
        onSuccess: () => {
          toast.success('Preferences saved');
          void queryClient.invalidateQueries({ queryKey: ['f', 'stats'] });
        },
        onError: (error: unknown) =>
          toast.error(extractErrorMessage(error, 'Failed to save preferences')),
      }
    );
  }

  function handleThemeChange(next: Theme) {
    if (next === theme) return;
    setTheme(next);
    toast.success(`Theme set to ${next}`);
  }

  if (isLoading) {
    return (
      <motion.section
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1, ease: EASE }}
      >
        <div className="animate-pulse space-y-4">
          <div
            className="h-6 w-32 rounded-md"
            style={{ background: 'var(--color-surface-muted)' }}
          />
          <div className="h-48 rounded-2xl" style={{ background: 'var(--color-surface-muted)' }} />
        </div>
      </motion.section>
    );
  }

  return (
    <SettingsSection title="Preferences" delay={0.1} cardClassName="space-y-8">
      {/* Theme */}
      <div>
        <p className="text-list-title mb-1">Appearance</p>
        <p className="text-meta-muted mb-4">Choose how Formency looks for you</p>

        <div className="grid grid-cols-3 gap-3">
          {THEME_OPTIONS.map(({ value, label, description, icon: Icon }) => {
            const isActive = theme === value;
            const colors = previewColors[value];
            return (
              <button
                key={value}
                type="button"
                onClick={() => handleThemeChange(value)}
                className={[
                  'relative rounded-[20px] p-3.5 text-left transition-all duration-150 cursor-pointer',
                  isActive
                    ? ''
                    : 'hover:-translate-y-[1px] hover:border-[var(--color-border-strong)] hover:shadow-[var(--control-shadow-hover)]',
                ].join(' ')}
                style={{
                  background: isActive
                    ? 'var(--color-accent-wash-strong)'
                    : 'var(--color-surface-muted)',
                  border: isActive
                    ? '1px solid var(--color-accent-outline)'
                    : '1px solid var(--color-border)',
                  boxShadow: isActive ? 'var(--shadow-sm)' : 'none',
                }}
              >
                {/* Mini UI preview */}
                <div
                  className="mb-3 rounded-[18px] p-2.5"
                  style={{
                    backgroundColor: colors.bg,
                    border: `1px solid ${isActive ? 'var(--color-accent-outline)' : 'var(--color-surface-border)'}`,
                    boxShadow: 'inset 0 0.5px 0 rgba(255,255,255,0.4)',
                  }}
                >
                  <div
                    className="space-y-1.5 rounded-[14px] p-2.5"
                    style={{ backgroundColor: colors.surface }}
                  >
                    <div
                      className="h-1.5 rounded-full"
                      style={{ backgroundColor: colors.text, opacity: 0.6, width: '72%' }}
                    />
                    <div
                      className="h-1.5 rounded-full"
                      style={{ backgroundColor: colors.text, opacity: 0.3, width: '50%' }}
                    />
                    <div
                      className="h-1.5 rounded-full"
                      style={{ backgroundColor: colors.accent, width: '40%' }}
                    />
                  </div>
                </div>

                {/* Label + description */}
                <div className="mb-0.5 flex items-center gap-1.5">
                  <Icon
                    size={13}
                    strokeWidth={2}
                    style={{
                      color: isActive ? 'var(--color-accent-text)' : 'var(--color-text-muted)',
                    }}
                  />
                  <span className="text-card-title">{label}</span>
                </div>
                <p className="text-meta-muted">{description}</p>

                {/* Active check */}
                {isActive && (
                  <div
                    className="absolute top-2.5 right-2.5 h-5 w-5 rounded-full flex items-center justify-center"
                    style={{ background: 'var(--color-accent)' }}
                  >
                    <Check
                      size={12}
                      strokeWidth={2.5}
                      style={{ color: 'var(--color-text-on-accent)' }}
                    />
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Divider */}
      <div style={{ height: 1, background: 'var(--color-border)' }} />

      {/* Timezone & week start form */}
      <Form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div>
          <p className="text-list-title mb-1">Timezone</p>
          <p className="text-meta-muted mb-3">Used for streak tracking and activity reporting</p>
          <div className="max-w-sm">
            <FormComboBox
              control={control}
              name="timezone"
              aria-label="Timezone"
              placeholder="Search timezones..."
              items={timezoneItems}
            />
          </div>
          {watchedTimezone === 'auto' && (
            <p className="text-meta-muted mt-2">Currently detected: {detectedTimezone}</p>
          )}
        </div>

        <div>
          <p className="text-list-title mb-1">Week starts on</p>
          <p className="text-meta-muted mb-3">
            Used for weekly statistics like &quot;lessons this week&quot;
          </p>
          <div className="flex gap-2">
            {(['monday', 'sunday'] as const).map((day) => {
              const isActive = watchedWeekStart === day;
              return (
                <Button
                  key={day}
                  type="button"
                  size="sm"
                  variant={isActive ? 'accent' : 'secondary'}
                  onPress={() => setValue('weekStartsOn', day, { shouldDirty: true })}
                  aria-pressed={isActive}
                  style={{ textTransform: 'capitalize' }}
                >
                  {day}
                </Button>
              );
            })}
          </div>
        </div>

        {isDirty && (
          <div className="flex gap-3 pt-1">
            <Button type="submit" isDisabled={updateMutation.isPending} variant="accent">
              {updateMutation.isPending ? 'Saving...' : 'Save changes'}
            </Button>
            <Button
              onPress={() => {
                reset();
                updateMutation.reset();
              }}
              isDisabled={updateMutation.isPending}
              variant="ghost"
            >
              Cancel
            </Button>
          </div>
        )}
      </Form>
    </SettingsSection>
  );
}
